/**
 * Build the EIP-712 typed-data payload for an ERC-3009
 * `TransferWithAuthorization` signature. This is what the x402
 * "exact" scheme requires the payer to sign.
 *
 * The resulting object shape matches wagmi's `useSignTypedData`
 * input (domain + types + primaryType + message). The facilitator
 * consumes the raw signature + authorization back in the POST.
 *
 * Source fields come straight from the `/info` response:
 *   - `link.tokenName` / `link.tokenVersion` / `link.chainId` /
 *     `link.tokenAddress` → EIP-712 `domain`.
 *   - `link.payTo` / `link.amount` → ERC-3009 `to` / `value`.
 *   - Caller supplies `fromAddress` (connected wallet) and we
 *     generate `validAfter` / `validBefore` / `nonce` locally.
 *
 * Nonce is 32 random bytes — ERC-3009 uses the nonce to make each
 * authorization single-use, independent of ERC-20 `permit`'s
 * sequential nonces. Collision probability on 2^256 is negligible;
 * the token contract replay-protects via its internal `nonce_used`
 * bitmap.
 *
 * Validity window: `validAfter = 0` (immediate), `validBefore` = +1h
 * from now. The facilitator needs time to land the tx + account for
 * clock skew; 1h is the x402 convention. Callers who want a
 * different window can pass `validBeforeSeconds` override.
 */

/** Generate a cryptographically-random 32-byte nonce as 0x-hex. Uses
 *  the browser Web Crypto API; falls back to `Math.random` only in
 *  a test / SSR context where `crypto` is missing (never ships to
 *  browsers — Chrome ≥ 37 has always had `crypto.getRandomValues`). */
export function generateAuthorizationNonce() {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Dev / Jest fallback. Never runs in production.
    for (let i = 0; i < 32; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let hex = "0x";
  for (let i = 0; i < 32; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

const ERC_3009_TYPES = {
  TransferWithAuthorization: [
    { name: "from", type: "address" },
    { name: "to", type: "address" },
    { name: "value", type: "uint256" },
    { name: "validAfter", type: "uint256" },
    { name: "validBefore", type: "uint256" },
    { name: "nonce", type: "bytes32" },
  ],
};

/**
 * Build the typed-data input for wagmi's `useSignTypedData`.
 *
 * `link` is the PaymentLinkInfo object returned by `/info`.
 * `fromAddress` is the connected wallet's EVM address (checksummed
 * or not — the facilitator normalizes).
 *
 * Returned shape is consumable directly by `signTypedDataAsync`:
 *   await signTypedDataAsync(buildTypedData(link, address));
 *
 * Plus we return `message` separately so the caller can reuse it
 * verbatim as the `authorization` field in the x402 payload (no
 * need to reconstruct from the signature side).
 */
export function buildTypedData(link, fromAddress, options = {}) {
  const nowSec = Math.floor(Date.now() / 1000);
  const validBefore = options.validBeforeSeconds
    ? nowSec + Number(options.validBeforeSeconds)
    : nowSec + 3600; // 1h default

  // All uint256 fields are stringified. EIP-712 signing normalises
  // both number and string inputs to the same 32-byte hash, so the
  // signature is unaffected. On the wire, however, the x402 "exact"
  // scheme spec requires these fields as strings — facilitators that
  // validate the payload schema strictly will reject a JSON number
  // for `value` / `validAfter` / `validBefore`. Matching the spec
  // here avoids a class of `invalid_exact_evm_transaction_*` errors
  // that are really just payload-shape disagreements.
  const message = {
    from: fromAddress,
    to: link.payTo,
    value: String(link.amount),
    validAfter: "0",
    validBefore: String(validBefore),
    nonce: generateAuthorizationNonce(),
  };

  return {
    domain: {
      name: link.tokenName,
      version: link.tokenVersion,
      // `chainId` from /info is a number (e.g. 84532 for Base
      // Sepolia). wagmi requires number; no coercion needed.
      chainId: Number(link.chainId),
      verifyingContract: link.tokenAddress,
    },
    types: ERC_3009_TYPES,
    primaryType: "TransferWithAuthorization",
    message,
  };
}
